// @flow

import React, { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { pipe, flatten, reject, not } from 'ramda'

import { SideMenu, MENU_ITEMS } from '@alphaott/smart-tv-sidebar'
import {
  getIsAvailableLiveTv,
  getIsAvailableTvShows,
  getIsAvailableMovies,
  getCustomer,
  getCustomerId,
  getCustomerGuestMode,
  isNewCustomerAndWithoutServices,
  getIsAvailableRadio,
  getIsAvailablePersonalLists,
} from '@alphaott/app-core/selectors'
import { getEnabledSmartTVEpg } from '@alphaott/smart-tv-epg'
import { useDeviceDataForAnalytics } from '@alphaott/smart-tv-current-device/hooks'
import { WelcomeModal } from '@alphaott/smart-tv-notifications'
import { isEditProfileEnabled as getEditProfileEnabled } from '@alphaott/app-main/selectors'

import { useExitAppModal } from '@alphaott/smart-tv-notifications/hooks'
import { useAnalyticsIdentify } from '@alphaott/app-analytics'
import { isWebOS } from '@alphaott/smart-tv-platforms'

import { useDemoModal } from '../../hooks'

type Props = {
  children: any,
}

const { SEARCH, HOME, LIVE_TV, TV_GUIDE, MOVIES, TV_SHOWS, RADIO, MY_LIST, SETTINGS } = MENU_ITEMS

export const App = ({ children }: Props) => {
  const navigate = useNavigate()

  const { handleOpenExitAppModal, handleApproveExitAppModal } = useExitAppModal()
  const currentDevice = useDeviceDataForAnalytics()

  const customer = useSelector(getCustomer)
  const customerId = useSelector(getCustomerId)
  const isGuestCustomer = useSelector(getCustomerGuestMode)

  useAnalyticsIdentify(customerId, { ...currentDevice, isGuestCustomer })

  const isNewCustomer = useSelector(isNewCustomerAndWithoutServices)
  const isAvailablePersonalLists = useSelector(getIsAvailablePersonalLists)

  const isEpg = useSelector(getEnabledSmartTVEpg)

  const isAvailableLiveTv = useSelector(getIsAvailableLiveTv)
  const isAvailableTvShows = useSelector(getIsAvailableTvShows)
  const isAvailableMovies = useSelector(getIsAvailableMovies)
  const isAvailableRadio = useSelector(getIsAvailableRadio)

  const isAllowedSearch = useMemo(() => not(isNewCustomer) && SEARCH, [isNewCustomer])
  const isAllowedLiveTV = useMemo(() => isAvailableLiveTv && LIVE_TV, [isAvailableLiveTv])
  const isAllowedEpg = useMemo(() => isEpg && TV_GUIDE, [isEpg])
  const isAllowedMovies = useMemo(() => isAvailableMovies && MOVIES, [isAvailableMovies])
  const isAllowedTVShows = useMemo(() => isAvailableTvShows && TV_SHOWS, [isAvailableTvShows])
  const isAllowedMyList = useMemo(
    () => not(isNewCustomer) && isAvailablePersonalLists && MY_LIST,
    [isNewCustomer, isAvailablePersonalLists],
  )
  const isAllowedRadio = useMemo(() => isAvailableRadio && RADIO, [isAvailableRadio])

  const isEditProfileEnabled = useSelector(getEditProfileEnabled)

  const { isOpenDemoWelcomeModal, subscriptionLink, handleCloseDemoWelcomeScreen } = useDemoModal()

  const subMenuItems = useMemo(
    () =>
      pipe(
        flatten,
        reject(not),
      )([
        isAllowedSearch,
        HOME,
        isAllowedLiveTV,
        isAllowedEpg,
        isAllowedMovies,
        isAllowedTVShows,
        isAllowedRadio,
        isAllowedMyList,
        SETTINGS,
      ]),
    [
      isAllowedSearch,
      isAllowedLiveTV,
      isAllowedEpg,
      isAllowedMovies,
      isAllowedTVShows,
      isAllowedRadio,
      isAllowedMyList,
    ],
  )

  const handleExitApp = useCallback(() => {
    if (isWebOS) {
      return handleApproveExitAppModal()
    }

    return handleOpenExitAppModal()
  }, [handleApproveExitAppModal, handleOpenExitAppModal])

  const handlePressItem = useCallback((route) => navigate(route), [navigate])

  const handleAccount = useCallback(() => navigate('/account'), [navigate])

  return (
    <div style={{ display: 'flex', flex: 1 }}>
      <SideMenu
        subMenuItems={subMenuItems}
        customer={customer}
        onAccount={handleAccount}
        onClickSubMenuItem={handlePressItem}
        onExit={handleExitApp}
      />

      {children}

      {isOpenDemoWelcomeModal && (
        <WelcomeModal
          onCloseModal={handleCloseDemoWelcomeScreen}
          link={subscriptionLink}
          {...(!isEditProfileEnabled && {
            description:
              'mwstv_notifications_welcome_message_no_active_subscriptions_please_contact_support_message',
          })}
        />
      )}
    </div>
  )
}

export default App
